import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { Link } from 'react-router-dom';
import { AnimatePresence, motion, useReducedMotion } from 'framer-motion';
import {
  ArrowUp,
  Check,
  RotateCcw,
  Sparkles,
  X,
  AlertCircle,
} from 'lucide-react';
import { toast } from 'sonner';
import { MarkdownMessage } from '@/components/ai/MarkdownMessage';
import { QuickActionChips } from '@/components/ai/QuickActionChips';
import { Monogram as BrandMonogram } from '@/components/brand/Logo';
import { WhatsAppGlyph } from '@/components/site/WhatsAppButton';
import { AGENCY, CONSULTATION_PATH, whatsappLink } from '@/lib/site';
import { useI18n, interpolate } from '@/lib/i18n';
import { saveAiLead, streamChat, summarizeConversation } from '@/lib/api';
import { cn } from '@/lib/utils';

const EASE = [0.22, 1, 0.36, 1];
const SESSION_KEY = 'rmh_ai_session_id';
const MESSAGES_KEY = 'rmh_ai_messages';
const LEAD_KEY = 'rmh_ai_lead_saved';

/**
 * Visitor turns at which we ask the backend to read the transcript and upsert
 * a lead.
 *
 * Plenty of visitors type their name and e-mail straight into the chat instead
 * of using the capture card — without this, those enquiries would never reach
 * the dashboard. Fixed checkpoints (rather than every turn) keep the number of
 * extraction calls, and therefore the cost, predictable. The backend discards
 * anything that is neither contactable nor qualified, and never overwrites
 * details that were already captured.
 */
const SUMMARY_CHECKPOINTS = [3, 6, 10];

const newSessionId = () =>
  `rmh-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 9)}`;

const readStored = (key, fallback) => {
  try {
    const raw = window.localStorage.getItem(key);
    return raw ? JSON.parse(raw) : fallback;
  } catch {
    return fallback;
  }
};

const writeStored = (key, value) => {
  try {
    window.localStorage.setItem(key, JSON.stringify(value));
  } catch {
    /* storage unavailable — conversation simply won't persist */
  }
};

/* ------------------------------------------------------------------ pieces */

const Monogram = ({ size = 'h-6' }) => (
  <BrandMonogram className={cn(size, 'w-auto')} />
);

const TypingDots = ({ label }) => (
  <div
    className="inline-flex items-center gap-2 rounded-2xl border border-[color:var(--rmh-border)] px-4 py-3"
    style={{ backgroundColor: 'var(--rmh-surface-2)' }}
    data-testid="romilda-ai-typing-indicator"
  >
    <span className="flex items-center gap-1">
      {[0, 1, 2].map((i) => (
        <span
          key={i}
          className="rmh-dot block h-[5px] w-[5px] rounded-full"
          style={{
            backgroundColor: 'var(--rmh-accent)',
            animationDelay: `${i * 0.18}s`,
          }}
        />
      ))}
    </span>
    <span className="text-[0.72rem] uppercase tracking-[0.1em] text-[color:var(--rmh-muted)]">
      {label}
    </span>
  </div>
);

const InChatCtas = () => {
  const { t } = useI18n();

  return (
    <div className="mt-3 flex flex-wrap gap-2">
      <Link
        to={CONSULTATION_PATH}
        data-testid="romilda-ai-book-consultation-button"
        className="inline-flex h-9 items-center gap-2 rounded-[10px] px-4 text-[0.76rem] font-medium tracking-[0.02em] transition-colors duration-300"
        style={{
          backgroundColor: 'var(--rmh-ink)',
          color: 'var(--rmh-canvas)',
        }}
      >
        {t.common.bookConsultation}
      </Link>
      <a
        href={whatsappLink(t.whatsappMessage)}
        target="_blank"
        rel="noopener noreferrer"
        data-testid="romilda-ai-whatsapp-link"
        className="inline-flex h-9 items-center gap-2 rounded-[10px] border border-[color:var(--rmh-border-strong)] px-4 text-[0.76rem] font-medium text-[color:var(--rmh-ink)] transition-colors duration-300 hover:border-[color:var(--rmh-ink)]"
        style={{ backgroundColor: 'var(--rmh-surface)' }}
      >
        <WhatsAppGlyph className="h-3.5 w-3.5" />
        {t.common.whatsapp}
      </a>
    </div>
  );
};

const LeadCaptureCard = ({ sessionId, onDone }) => {
  const { t, lang } = useI18n();
  const copy = t.widget.lead;
  const [form, setForm] = useState({ name: '', email: '', focus: '' });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email.trim())) {
      setError(copy.error);
      return;
    }
    setSaving(true);
    setError('');
    try {
      await saveAiLead({
        name: form.name.trim(),
        email: form.email.trim(),
        focus: form.focus || null,
        session_id: sessionId,
        lang,
      });
      writeStored(LEAD_KEY, true);
      toast.success(copy.toast);
      onDone();
    } catch {
      setError(copy.failed);
    } finally {
      setSaving(false);
    }
  };

  const field =
    'h-10 w-full rounded-[10px] border border-[color:var(--rmh-border)] px-3 text-[0.82rem] text-[color:var(--rmh-ink)] outline-none transition-colors duration-300 focus:border-[color:var(--rmh-accent)]';

  return (
    <form
      onSubmit={submit}
      data-testid="romilda-ai-lead-capture-form"
      className="rounded-[14px] border p-4"
      style={{
        backgroundColor: 'var(--rmh-surface)',
        borderColor: 'var(--rmh-border-strong)',
      }}
    >
      <div className="flex items-start justify-between gap-3">
        <div>
          <p className="rmh-eyebrow text-[0.58rem]">{copy.eyebrow}</p>
          <p className="mt-2 font-serif text-[0.98rem] leading-snug text-[color:var(--rmh-ink)]">
            {copy.title}
          </p>
        </div>
        <button
          type="button"
          onClick={onDone}
          aria-label={copy.dismissAria}
          data-testid="romilda-ai-lead-capture-dismiss-button"
          className="shrink-0 text-[color:var(--rmh-muted-2)] transition-colors duration-300 hover:text-[color:var(--rmh-ink)]"
        >
          <X className="h-3.5 w-3.5" />
        </button>
      </div>

      <p className="mt-2 text-[0.76rem] leading-relaxed text-[color:var(--rmh-muted)]">
        {copy.body}
      </p>

      <div className="mt-4 flex flex-col gap-2.5">
        <input
          type="text"
          value={form.name}
          onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
          placeholder={copy.namePlaceholder}
          aria-label={copy.namePlaceholder}
          data-testid="romilda-ai-lead-name-input"
          className={field}
          style={{ backgroundColor: 'var(--rmh-surface-inset)' }}
        />
        <input
          type="email"
          value={form.email}
          onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))}
          placeholder={copy.emailPlaceholder}
          aria-label={copy.emailPlaceholder}
          data-testid="romilda-ai-lead-email-input"
          className={field}
          style={{ backgroundColor: 'var(--rmh-surface-inset)' }}
        />
        <select
          value={form.focus}
          onChange={(e) => setForm((f) => ({ ...f, focus: e.target.value }))}
          aria-label={copy.focusPlaceholder}
          data-testid="romilda-ai-lead-focus-select"
          className={cn(field, 'appearance-none')}
          style={{ backgroundColor: 'var(--rmh-surface-inset)' }}
        >
          <option value="">{copy.focusPlaceholder}</option>
          {['marketing', 'tech', 'other']
            .map((key) => ({
              key,
              label: t.serviceOptionGroups[key],
              options: t.serviceOptions.filter(
                (o) => (o.group || 'other') === key,
              ),
            }))
            .filter((g) => g.options.length > 0)
            .map((group) => (
              <optgroup key={group.key} label={group.label}>
                {group.options.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </optgroup>
            ))}
        </select>
      </div>

      {error ? (
        <p
          className="mt-2.5 flex items-center gap-1.5 text-[0.74rem]"
          style={{ color: 'var(--rmh-mocha)' }}
          data-testid="romilda-ai-lead-error"
        >
          <AlertCircle className="h-3.5 w-3.5" />
          {error}
        </p>
      ) : null}

      <button
        type="submit"
        disabled={saving}
        data-testid="romilda-ai-lead-capture-submit-button"
        className="mt-4 inline-flex h-10 w-full items-center justify-center gap-2 rounded-[10px] text-[0.8rem] font-medium transition-colors duration-300 disabled:opacity-60"
        style={{ backgroundColor: 'var(--rmh-ink)', color: 'var(--rmh-canvas)' }}
      >
        {saving ? t.common.sending : copy.submit}
      </button>
    </form>
  );
};

/* ------------------------------------------------------------------ widget */

export const RomildaAIWidget = () => {
  const reduce = useReducedMotion();
  const { t, lang } = useI18n();
  const w = t.widget;

  const [open, setOpen] = useState(false);
  const [sessionId, setSessionId] = useState('');
  const [messages, setMessages] = useState([]);
  const [draft, setDraft] = useState('');
  const [streaming, setStreaming] = useState(false);
  const [streamText, setStreamText] = useState('');
  const [error, setError] = useState('');
  const [leadSaved, setLeadSaved] = useState(false);
  const [leadDismissed, setLeadDismissed] = useState(false);

  const scrollRef = useRef(null);
  const inputRef = useRef(null);
  const abortRef = useRef(null);
  const pinnedRef = useRef(true);

  /* ---- session bootstrap (survives refresh) ---- */
  useEffect(() => {
    let sid = readStored(SESSION_KEY, null);
    if (!sid) {
      sid = newSessionId();
      writeStored(SESSION_KEY, sid);
    }
    setSessionId(sid);
    setMessages(readStored(MESSAGES_KEY, []));
    setLeadSaved(Boolean(readStored(LEAD_KEY, false)));
  }, []);

  useEffect(() => {
    if (sessionId) writeStored(MESSAGES_KEY, messages);
  }, [messages, sessionId]);

  /* ---- autoscroll (only when the reader is already near the bottom) ---- */
  const scrollToBottom = useCallback((force = false) => {
    const el = scrollRef.current;
    if (!el) return;
    if (force || pinnedRef.current) {
      el.scrollTop = el.scrollHeight;
    }
  }, []);

  useEffect(() => {
    scrollToBottom();
  }, [messages, streamText, scrollToBottom]);

  const onScroll = () => {
    const el = scrollRef.current;
    if (!el) return;
    pinnedRef.current = el.scrollHeight - el.scrollTop - el.clientHeight < 80;
  };

  /* ---- open / close ---- */
  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => {
      if (e.key === 'Escape') setOpen(false);
    };
    window.addEventListener('keydown', onKey);
    const timer = setTimeout(() => {
      inputRef.current?.focus();
      scrollToBottom(true);
    }, 220);
    return () => {
      window.removeEventListener('keydown', onKey);
      clearTimeout(timer);
    };
  }, [open, scrollToBottom]);

  useEffect(() => () => abortRef.current?.abort(), []);

  const aiReplyCount = useMemo(
    () => messages.filter((m) => m.role === 'assistant').length,
    [messages],
  );

  const showCtas = aiReplyCount >= 2;
  const showLeadForm = aiReplyCount >= 3 && !leadSaved && !leadDismissed;

  const summarisedAtRef = useRef(new Set());

  /* ---- send ---- */
  const send = useCallback(
    async (text) => {
      const clean = (text || '').trim();
      if (!clean || streaming || !sessionId) return;

      setError('');
      setDraft('');
      pinnedRef.current = true;

      const history = messages.map((m) => ({ role: m.role, content: m.content }));
      setMessages((prev) => [...prev, { role: 'user', content: clean, ts: Date.now() }]);
      setStreaming(true);
      setStreamText('');

      const controller = new AbortController();
      abortRef.current = controller;

      try {
        const full = await streamChat({
          sessionId,
          message: clean,
          history,
          lang,
          signal: controller.signal,
          onDelta: (_chunk, assembled) => setStreamText(assembled),
        });
        setMessages((prev) => [
          ...prev,
          { role: 'assistant', content: full, ts: Date.now() },
        ]);

        // Fire-and-forget: never let lead capture block or break the chat.
        const turn = history.filter((m) => m.role === 'user').length + 1;
        if (
          SUMMARY_CHECKPOINTS.includes(turn) &&
          !summarisedAtRef.current.has(turn)
        ) {
          summarisedAtRef.current.add(turn);
          summarizeConversation(sessionId).catch(() => {
            summarisedAtRef.current.delete(turn);
          });
        }
      } catch (err) {
        if (err?.name !== 'AbortError') {
          setError(
            err?.friendly ||
              interpolate(w.unavailable, { email: AGENCY.email }),
          );
        }
      } finally {
        setStreaming(false);
        setStreamText('');
        abortRef.current = null;
      }
    },
    [messages, sessionId, streaming, lang, w.unavailable],
  );

  const reset = () => {
    abortRef.current?.abort();
    const sid = newSessionId();
    writeStored(SESSION_KEY, sid);
    writeStored(MESSAGES_KEY, []);
    setSessionId(sid);
    setMessages([]);
    summarisedAtRef.current = new Set();
    setStreamText('');
    setStreaming(false);
    setError('');
    setLeadDismissed(false);
    setTimeout(() => inputRef.current?.focus(), 80);
  };

  const onKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      send(draft);
    }
  };

  const panelMotion = reduce
    ? {}
    : {
        initial: { opacity: 0, y: 18, scale: 0.985 },
        animate: { opacity: 1, y: 0, scale: 1 },
        exit: { opacity: 0, y: 12, scale: 0.985 },
        transition: { duration: 0.34, ease: EASE },
      };

  return (
    <>
      {/* ------------------------------- launcher ------------------------------- */}
      <AnimatePresence>
        {!open ? (
          <motion.div
            key="launcher"
            initial={reduce ? undefined : { opacity: 0, y: 14 }}
            animate={reduce ? undefined : { opacity: 1, y: 0 }}
            exit={reduce ? undefined : { opacity: 0, y: 14 }}
            transition={{ duration: 0.4, ease: EASE, delay: 0.5 }}
            className="fixed bottom-5 right-4 z-[60] sm:bottom-6 sm:right-6"
          >
            <button
              type="button"
              onClick={() => setOpen(true)}
              data-testid="romilda-ai-launcher-button"
              aria-label={w.launcherAria}
              className="rmh-attention group flex items-center gap-3 rounded-full border py-2 pl-2 pr-4 transition-[border-color,box-shadow,transform] duration-500 hover:-translate-y-[2px] sm:pr-5"
              style={{
                backgroundColor: 'var(--rmh-surface)',
                borderColor: 'var(--rmh-border-strong)',
                boxShadow: 'var(--shadow-soft)',
              }}
            >
              <span
                className="relative flex h-9 w-9 shrink-0 items-center justify-center rounded-full border sm:h-10 sm:w-10"
                style={{
                  backgroundColor: 'var(--rmh-surface-inset)',
                  borderColor: 'var(--rmh-border)',
                }}
              >
                <Monogram size="h-4 sm:h-[18px]" />
                <span
                  aria-hidden="true"
                  className="absolute -right-[1px] -top-[1px] block h-2 w-2 rounded-full ring-2"
                  style={{
                    backgroundColor: 'var(--rmh-accent)',
                    ['--tw-ring-color']: 'var(--rmh-surface)',
                  }}
                />
              </span>
              <span className="flex flex-col items-start leading-none">
                <span className="font-serif text-[0.92rem] text-[color:var(--rmh-ink)] sm:text-[0.98rem]">
                  {w.launcherTitle}
                </span>
                <span className="mt-[3px] text-[0.58rem] font-medium uppercase tracking-[0.18em] text-[color:var(--rmh-muted)]">
                  {w.launcherSubtitle}
                </span>
              </span>
            </button>
          </motion.div>
        ) : null}
      </AnimatePresence>

      {/* -------------------------------- panel -------------------------------- */}
      <AnimatePresence>
        {open ? (
          <>
            {/* mobile scrim */}
            <motion.div
              key="scrim"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.25 }}
              onClick={() => setOpen(false)}
              className="fixed inset-0 z-[59] sm:hidden"
              style={{ backgroundColor: 'rgba(20,19,18,0.42)' }}
              aria-hidden="true"
            />

            <motion.div
              key="panel"
              {...panelMotion}
              role="dialog"
              aria-modal="true"
              aria-label={w.panelAria}
              data-testid="romilda-ai-panel"
              className={cn(
                'fixed z-[61] flex flex-col overflow-hidden border',
                'inset-x-3 bottom-3 top-[4.5rem] rounded-[18px]',
                'sm:inset-auto sm:bottom-6 sm:right-6 sm:top-auto sm:h-[640px] sm:max-h-[calc(100vh-6rem)] sm:w-[420px] sm:rounded-[18px]',
              )}
              style={{
                backgroundColor: 'var(--rmh-canvas)',
                borderColor: 'var(--rmh-border-strong)',
                boxShadow: 'var(--shadow-lift)',
              }}
            >
              <span
                aria-hidden="true"
                className="absolute inset-x-0 top-0 h-[2px]"
                style={{
                  backgroundImage:
                    'linear-gradient(90deg, rgba(201,176,138,0) 0%, var(--rmh-accent) 50%, rgba(201,176,138,0) 100%)',
                }}
              />

              {/* header */}
              <div
                className="flex shrink-0 items-center justify-between gap-3 border-b px-4 py-3.5"
                style={{
                  backgroundColor: 'var(--rmh-surface)',
                  borderColor: 'var(--rmh-border)',
                }}
              >
                <div className="flex items-center gap-3">
                  <span
                    className="flex h-10 w-10 items-center justify-center rounded-full border"
                    style={{
                      backgroundColor: 'var(--rmh-surface-inset)',
                      borderColor: 'var(--rmh-border)',
                    }}
                  >
                    <Monogram size="h-[18px]" />
                  </span>
                  <div className="leading-none">
                    <p className="font-serif text-[1.02rem] text-[color:var(--rmh-ink)]">
                      {w.launcherTitle}
                    </p>
                    <p className="mt-[5px] flex items-center gap-1.5 text-[0.62rem] font-medium uppercase tracking-[0.16em] text-[color:var(--rmh-muted)]">
                      <span
                        className="block h-1.5 w-1.5 rounded-full"
                        style={{ backgroundColor: 'var(--rmh-accent)' }}
                        aria-hidden="true"
                      />
                      Claude Sonnet 4.5
                    </p>
                  </div>
                </div>

                <div className="flex items-center gap-1">
                  {messages.length ? (
                    <button
                      type="button"
                      onClick={reset}
                      aria-label={w.resetAria}
                      title={w.resetAria}
                      data-testid="romilda-ai-reset-button"
                      className="inline-flex h-9 w-9 items-center justify-center rounded-full text-[color:var(--rmh-muted)] transition-colors duration-300 hover:bg-[color:var(--rmh-surface-inset)] hover:text-[color:var(--rmh-ink)]"
                    >
                      <RotateCcw className="h-[15px] w-[15px]" />
                    </button>
                  ) : null}
                  <button
                    type="button"
                    onClick={() => setOpen(false)}
                    aria-label={w.closeAria}
                    data-testid="romilda-ai-close-button"
                    className="inline-flex h-9 w-9 items-center justify-center rounded-full text-[color:var(--rmh-muted)] transition-colors duration-300 hover:bg-[color:var(--rmh-surface-inset)] hover:text-[color:var(--rmh-ink)]"
                  >
                    <X className="h-4 w-4" />
                  </button>
                </div>
              </div>

              {/* messages */}
              <div
                ref={scrollRef}
                onScroll={onScroll}
                className="rmh-scroll flex-1 overflow-y-auto px-4 py-5"
                data-testid="romilda-ai-messages"
              >
                {/* welcome */}
                <div className="flex flex-col items-start gap-3">
                  <div
                    className="max-w-[90%] rounded-2xl rounded-tl-md border px-4 py-3"
                    style={{
                      backgroundColor: 'var(--rmh-surface-2)',
                      borderColor: 'var(--rmh-border)',
                    }}
                    data-testid="romilda-ai-welcome-message"
                  >
                    <p className="font-serif text-[1.02rem] leading-snug text-[color:var(--rmh-ink)]">
                      {w.welcomeTitle}
                    </p>
                    <p className="mt-2 text-[0.86rem] leading-relaxed text-[color:var(--rmh-ink-2)]">
                      {w.welcomeBody}
                    </p>
                  </div>

                  {messages.length === 0 ? (
                    <div className="w-full">
                      <p className="rmh-eyebrow mt-2 text-[0.56rem]">{w.startHere}</p>
                      <QuickActionChips
                        actions={w.quickActions}
                        onSelect={(a) => send(a.prompt)}
                        disabled={streaming}
                        className="mt-3"
                      />
                    </div>
                  ) : null}
                </div>

                {/* transcript */}
                <div className="mt-5 flex flex-col gap-4">
                  {messages.map((m, i) =>
                    m.role === 'user' ? (
                      <div
                        key={`${m.ts}-${i}`}
                        className="flex justify-end"
                        data-testid={`romilda-ai-user-message-${i}`}
                      >
                        <div
                          className="max-w-[86%] rounded-2xl rounded-br-md px-4 py-2.5 text-[0.86rem] leading-relaxed"
                          style={{
                            backgroundColor: 'var(--rmh-ink)',
                            color: 'var(--rmh-canvas)',
                          }}
                        >
                          {m.content}
                        </div>
                      </div>
                    ) : (
                      <div
                        key={`${m.ts}-${i}`}
                        className="flex flex-col items-start"
                        data-testid={`romilda-ai-assistant-message-${i}`}
                      >
                        <div
                          className="max-w-[94%] rounded-2xl rounded-tl-md border px-4 py-3"
                          style={{
                            backgroundColor: 'var(--rmh-surface-2)',
                            borderColor: 'var(--rmh-border)',
                          }}
                        >
                          <MarkdownMessage content={m.content} />
                        </div>
                        {showCtas && i === messages.length - 1 && !streaming ? (
                          <InChatCtas />
                        ) : null}
                      </div>
                    ),
                  )}

                  {/* live stream */}
                  {streaming ? (
                    <div className="flex flex-col items-start">
                      {streamText ? (
                        <div
                          className="max-w-[94%] rounded-2xl rounded-tl-md border px-4 py-3"
                          style={{
                            backgroundColor: 'var(--rmh-surface-2)',
                            borderColor: 'var(--rmh-border)',
                          }}
                          data-testid="romilda-ai-streaming-message"
                        >
                          <MarkdownMessage content={streamText} />
                        </div>
                      ) : (
                        <TypingDots label={w.thinking} />
                      )}
                    </div>
                  ) : null}

                  {/* error */}
                  {error ? (
                    <div
                      className="rounded-[12px] border px-4 py-3"
                      style={{
                        backgroundColor: 'var(--rmh-surface)',
                        borderColor: 'var(--rmh-border-strong)',
                      }}
                      data-testid="romilda-ai-error-message"
                    >
                      <p className="flex items-start gap-2 text-[0.8rem] leading-relaxed text-[color:var(--rmh-ink-2)]">
                        <AlertCircle
                          className="mt-[2px] h-4 w-4 shrink-0"
                          style={{ color: 'var(--rmh-mocha)' }}
                        />
                        {error}
                      </p>
                      <div className="mt-3 flex flex-wrap gap-2">
                        <button
                          type="button"
                          onClick={() => {
                            const lastUser = [...messages]
                              .reverse()
                              .find((m) => m.role === 'user');
                            if (lastUser) {
                              setMessages((prev) => {
                                const idx = prev.lastIndexOf(lastUser);
                                return idx >= 0 ? prev.slice(0, idx) : prev;
                              });
                              setError('');
                              setTimeout(() => send(lastUser.content), 40);
                            } else {
                              setError('');
                            }
                          }}
                          data-testid="romilda-ai-retry-button"
                          className="inline-flex h-9 items-center rounded-[10px] px-4 text-[0.76rem] font-medium"
                          style={{
                            backgroundColor: 'var(--rmh-ink)',
                            color: 'var(--rmh-canvas)',
                          }}
                        >
                          {t.common.tryAgain}
                        </button>
                        <a
                          href={`mailto:${AGENCY.email}`}
                          data-testid="romilda-ai-email-fallback-link"
                          className="inline-flex h-9 items-center rounded-[10px] border border-[color:var(--rmh-border-strong)] px-4 text-[0.76rem] font-medium text-[color:var(--rmh-ink)]"
                          style={{ backgroundColor: 'var(--rmh-surface)' }}
                        >
                          {w.emailInstead}
                        </a>
                      </div>
                    </div>
                  ) : null}

                  {/* inline lead capture */}
                  {showLeadForm && !streaming ? (
                    <LeadCaptureCard
                      sessionId={sessionId}
                      onDone={() => {
                        setLeadDismissed(true);
                        setLeadSaved(Boolean(readStored(LEAD_KEY, false)));
                      }}
                    />
                  ) : null}

                  {leadSaved && aiReplyCount >= 3 ? (
                    <div
                      className="flex items-center gap-2 rounded-[12px] border px-4 py-3"
                      style={{
                        backgroundColor: 'var(--rmh-surface)',
                        borderColor: 'var(--rmh-border)',
                      }}
                      data-testid="romilda-ai-lead-saved-note"
                    >
                      <Check
                        className="h-4 w-4 shrink-0"
                        style={{ color: 'var(--rmh-accent-2)' }}
                      />
                      <p className="text-[0.78rem] text-[color:var(--rmh-muted)]">
                        {w.lead.saved}
                      </p>
                    </div>
                  ) : null}
                </div>

                {/* follow-up chips */}
                {messages.length > 0 && !streaming ? (
                  <div className="mt-6">
                    <div className="rmh-rule" />
                    <QuickActionChips
                      actions={w.quickActions.slice(0, 4)}
                      onSelect={(a) => send(a.prompt)}
                      disabled={streaming}
                      className="mt-4"
                    />
                  </div>
                ) : null}
              </div>

              {/* composer */}
              <div
                className="shrink-0 border-t px-3 py-3"
                style={{
                  backgroundColor: 'var(--rmh-surface)',
                  borderColor: 'var(--rmh-border)',
                }}
              >
                <div
                  className="flex items-end gap-2 rounded-[14px] border px-3 py-2"
                  style={{
                    backgroundColor: 'var(--rmh-surface-inset)',
                    borderColor: 'var(--rmh-border)',
                  }}
                >
                  <textarea
                    ref={inputRef}
                    rows={1}
                    value={draft}
                    onChange={(e) => {
                      setDraft(e.target.value);
                      e.target.style.height = 'auto';
                      e.target.style.height = `${Math.min(e.target.scrollHeight, 120)}px`;
                    }}
                    onKeyDown={onKeyDown}
                    placeholder={w.placeholder}
                    aria-label={w.inputAria}
                    data-testid="romilda-ai-message-input"
                    className="max-h-[120px] min-h-[26px] flex-1 resize-none bg-transparent text-[0.86rem] leading-relaxed text-[color:var(--rmh-ink)] outline-none placeholder:text-[color:var(--rmh-muted-2)]"
                  />
                  <button
                    type="button"
                    onClick={() => send(draft)}
                    disabled={!draft.trim() || streaming}
                    aria-label={w.sendAria}
                    data-testid="romilda-ai-send-button"
                    className="mb-[1px] inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-full transition-[background-color,opacity] duration-300 disabled:opacity-35"
                    style={{
                      backgroundColor: 'var(--rmh-ink)',
                      color: 'var(--rmh-canvas)',
                    }}
                  >
                    <ArrowUp className="h-4 w-4" />
                  </button>
                </div>
                <p className="mt-2 flex items-center gap-1.5 px-1 text-[0.62rem] tracking-[0.06em] text-[color:var(--rmh-muted-2)]">
                  <Sparkles className="h-3 w-3" aria-hidden="true" />
                  {w.disclaimer}
                </p>
              </div>
            </motion.div>
          </>
        ) : null}
      </AnimatePresence>
    </>
  );
};

export default RomildaAIWidget;
